<template>
  <div>
    <div
      class="d-flex justify-content-center"
      v-if="!is_tamaro_loaded"
    >
      <app-spinner size="3" />
    </div>

    <div v-show="is_tamaro_loaded">
      <div
        id="tamaro"
        ref="tamaro"
        :class="{ 'input--errors': hasErrors }"
      />

      <p
        class="input--errors input--error margin__t--6"
        v-if="v?.required?.$invalid"
      >
        {{ t('errors.input_radio_required') }}
      </p>
    </div>

    <payment-provider-tamaro-fields class="margin__t--12" />
  </div>
</template>

<script setup>
import { ref, computed, inject, defineAsyncComponent, watch } from 'vue'
import { useNotificationStore } from '@/stores/NotificationStore'
import { useProcedureNavigation, useProcedureBlockValidator } from '@/composables/modules/procedures'
import { useTamaro } from '@/vendors/integrations/tamaro'
import { useI18n } from '@/vendors/i18n'
import { whenever } from '@/utils/utils'
import { sameAs } from '@vuelidate/validators'
import { get as _get, set as _set, has as _has } from 'lodash'

import PaymentProviderTamaroFields from '&/modules/procedures/fragments/transaction/PaymentProviderTamaroFields'
const AppSpinner = defineAsyncComponent(() => import('&/atoms/AppSpinner'))

const { paymentMethods } = defineProps({
  paymentMethods: { type: Array, default: () => [] },
  v: Object
})

const procedure = inject('procedure')
const procedure_execution = inject('procedure_execution')
const external_validations = inject('external_validations')
const is_loading = inject('is_loading')

const { tamaro_fields, is_tamaro_loaded, tamaro_validation_errors, renderTamaroWidget, setTamaroPaymentMethods } = useTamaro()
const { t } = useI18n()
const { postModalNotification } = useNotificationStore()
const { nextRedirection } = useProcedureNavigation({ procedure, procedure_execution })
useProcedureBlockValidator({ validations: { sameAs: sameAs(undefined) }, state: tamaro_validation_errors })

const tamaro = ref()

const dependenciesAreReady = computed(() => !is_loading.value && tamaro.value && is_tamaro_loaded.value)
const hasErrors = computed(() => Object.keys(tamaro_validation_errors.value || {}).some(error => !error.includes('stored_')))

whenever(dependenciesAreReady, () => {
  try {
    renderTamaroWidget('#tamaro')
  } catch {
    postModalNotification({ title: 'errors.an_error_occurred', code: '#001504', error: 'Tamaro failed to render', close: nextRedirection })
  }
}, { once: true })

watch(() => paymentMethods, () => setTamaroPaymentMethods(paymentMethods.map(paymentMethod => paymentMethod.value)), { immediate: true })

watch(tamaro_validation_errors, () => {
  external_validations.value = Object.keys(tamaro_validation_errors.value || {})
    .filter(key => _has(tamaro_fields, key))
    .reduce((validations, key) => _set(validations, _get(tamaro_fields, `${key}.model`), tamaro_validation_errors.value[key].map(({ code }) => `external:errors.tamaro_${code}`).find(v => v)), {})
}, { deep: true })
</script>

<style lang="scss" scoped>
#tamaro.input--errors {
  padding: 0;
  background: none;
}
</style>